import type { Questionnaire } from "@medplum/fhirtypes";
import { catalogNameToFhir, getTRPCClient, PERFORMANCE_MEASURE_DEFINITION_KIND, syncKey } from "@repo/api";
import {
  type ChangeQuestionnaireDefinitionAvailabilityInput,
  type CorrectQuestionnaireDefinitionInput,
  changeQuestionnaireDefinitionAvailabilityInputSchema,
  correctQuestionnaireDefinitionInputSchema,
  type DefineQuestionnaireDefinitionInput,
  defineQuestionnaireDefinitionInputSchema,
  type ReviseQuestionnaireDefinitionInput,
  reviseQuestionnaireDefinitionInputSchema,
} from "@repo/api/schemas";
import type { QuestionnaireScoreCalculation } from "@repo/domain";
import { mintEntityId, TOTAL_SCORE_ID } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type {
  QuestionnaireDefinitionAvailability,
  QuestionnaireSection,
} from "../fhir/questionnaire-definition-storage";
import { readPerformanceMeasureDefinitionRow } from "../fhir/questionnaire-definition-storage";
import type { QuestionnaireRow } from "../fhir/types";
import {
  buildOptimisticQuestionnaireScores,
  buildOptimisticQuestionnaireSections,
  optimisticQuestionnaireDefinitionRow,
} from "../lib/questionnaire-definition-optimistic";
import { projectQuestionnaireDefinitionRow } from "./questionnaire-definition-projection";

const COLLECTION = "questionnaire";
/** Every questionnaire canonical this app mints sits directly under this base. */
export const QUESTIONNAIRE_DEFINITION_CANONICAL_BASE = canonicalBase("Questionnaire");

/**
 * The stored structure, as the authoring surface knows it. Declared beside the
 * storage reader that produces it rather than a second time here (ADR-0021:
 * views read FHIR rows directly and never import codecs).
 */
export type {
  EnablementCondition,
  QuestionnaireAnswerValue,
  QuestionnaireCondition,
  QuestionnaireConditionValue,
  QuestionnaireDefinitionAvailability,
  QuestionnaireDisplay,
  QuestionnaireItem,
  QuestionnaireQuestion,
  QuestionnaireRepeatingGroup,
  QuestionnaireSection,
} from "../fhir/questionnaire-definition-storage";

export interface QuestionnaireDefinitionDto {
  url: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: QuestionnaireDefinitionAvailability;
  sections: readonly QuestionnaireSection[];
  marks: { section: number; item: number; option: number; score: number };
  scores: readonly {
    id: string;
    label: string;
    itemIds: readonly string[];
    bands?: readonly { label: string; from: number; to?: number }[];
    calculation?: QuestionnaireScoreCalculation;
  }[];
}
export interface QuestionnaireDefinitionFilters {
  availability?: QuestionnaireDefinitionAvailability;
  search?: string;
  includeHistorical?: boolean;
  orderBy?: "name" | "mintedAt";
  orderDirection?: "asc" | "desc";
}

export function mintQuestionnaireDefinitionUrl(): string {
  return canonicalUrl("Questionnaire", mintEntityId());
}

export function questionSetStore(performance: boolean) {
  const project = performance ? readPerformanceMeasureDefinitionRow : projectQuestionnaireDefinitionRow;
  const matches = (row: QuestionnaireRow) =>
    performance ? row.catalogModelKind === PERFORMANCE_MEASURE_DEFINITION_KIND : row.questionnaireDefinition;
  const optimisticRow = (dto: QuestionnaireDefinitionDto) =>
    optimisticQuestionnaireDefinitionRow(dto, undefined, performance);
  /**
   * Pure, and exported for it: a caller holding one revision's resource — an
   * as-of read, say — projects it without a collection or a live query, exactly
   * as every other catalog kind does.
   */
  function projectQuestionnaireDefinition(resource: Questionnaire): QuestionnaireDefinitionDto {
    return project(resource);
  }

  function useQuestionnaireDefinitions(filters: QuestionnaireDefinitionFilters = {}): QuestionnaireDefinitionDto[] {
    const collection = useCollection(COLLECTION);
    const status =
      filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
    const search = filters.search?.trim();
    const result = useLiveQuery({
      queryKey: [
        collection.id,
        performance,
        "questionnaire-definitions",
        status,
        search,
        filters.includeHistorical ?? false,
        filters.orderBy ?? "name",
        filters.orderDirection ?? (filters.orderBy === "mintedAt" ? "desc" : "asc"),
      ],
      query: (query) => {
        if (filters.includeHistorical) {
          const all = query
            .from({ definition: collection })
            .where(({ definition }) =>
              performance
                ? eq(definition.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
                : eq(definition.questionnaireDefinition, true),
            );
          const available = status ? all.where(({ definition }) => eq(definition.status, status)) : all;
          const matching = search
            ? available.where(({ definition }) =>
                or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
              )
            : available;
          const ordered =
            filters.orderBy === "mintedAt"
              ? matching.orderBy(({ definition }) => definition.date, filters.orderDirection ?? "desc")
              : matching.orderBy(({ definition }) => definition.title, filters.orderDirection ?? "asc");
          return (
            ordered
              .orderBy(({ definition }) => definition.catalogVersion, "desc")
              // TanStack DB cannot declaratively decode nested FHIR items/extensions;
              // keep that unsupported final transformation inside this live pipeline.
              .fn.select(({ definition }) => projectQuestionnaireDefinition(definition))
          );
        }
        const latest = query
          .from({ candidate: collection })
          .where(({ candidate }) =>
            performance
              ? eq(candidate.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
              : eq(candidate.questionnaireDefinition, true),
          )
          .groupBy(({ candidate }) => candidate.url)
          .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
        const current = query
          .from({ definition: collection })
          .join({ latest }, ({ definition, latest }) => eq(definition.url, latest.url))
          .where(({ definition, latest }) =>
            and(
              performance
                ? eq(definition.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
                : eq(definition.questionnaireDefinition, true),
              eq(definition.catalogVersion, latest.version),
            ),
          );
        const available = status ? current.where(({ definition }) => eq(definition.status, status)) : current;
        const matching = search
          ? available.where(({ definition }) =>
              or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
            )
          : available;
        const ordered =
          filters.orderBy === "mintedAt"
            ? matching.orderBy(({ definition }) => definition.date, filters.orderDirection ?? "desc")
            : matching.orderBy(({ definition }) => definition.title, filters.orderDirection ?? "asc");
        // TanStack DB cannot declaratively decode nested FHIR items/extensions;
        // keep that unsupported final transformation inside this live pipeline.
        return ordered.fn.select(({ definition }) => projectQuestionnaireDefinition(definition));
      },
    });
    return result.data ?? [];
  }

  function useQuestionnaireDefinition(canonical: string | undefined): QuestionnaireDefinitionDto | undefined {
    const collection = useCollection(COLLECTION);
    const separator = canonical?.lastIndexOf("|") ?? -1;
    const url = separator > -1 ? canonical?.slice(0, separator) : canonical;
    const version = separator > -1 ? Number(canonical?.slice(separator + 1)) : undefined;
    const result = useLiveQuery({
      queryKey: [collection.id, "questionnaire-definition", performance, url, version],
      query: (query) => {
        if (!url) return undefined;
        const lineage = query
          .from({ definition: collection })
          .where(({ definition }) =>
            and(
              performance
                ? eq(definition.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
                : eq(definition.questionnaireDefinition, true),
              eq(definition.url, url),
            ),
          );
        const selected =
          version === undefined ? lineage : lineage.where(({ definition }) => eq(definition.catalogVersion, version));
        return (
          selected
            .orderBy(({ definition }) => definition.catalogVersion, "desc")
            // TanStack DB cannot declaratively decode nested FHIR items/extensions;
            // keep that unsupported final transformation inside this live pipeline.
            .fn.select(({ definition }) => projectQuestionnaireDefinition(definition))
            .findOne()
        );
      },
    });
    return result.data;
  }

  const collection = () => getDataStoreClient().getCollection(COLLECTION);

  const defineQuestionnaireDefinition = defineWrite<DefineQuestionnaireDefinitionInput>({
    optimistic: (params) => {
      const input = defineQuestionnaireDefinitionInputSchema.parse(params);
      if (
        [...collection().state.values()].some(
          (candidate) => matches(candidate) && candidate.url === input.definitionUrl,
        )
      ) {
        throw new Error(`Questionnaire definition '${input.definitionUrl}' already exists`);
      }
      collection().insert(
        optimisticRow({
          url: input.definitionUrl,
          version: 1,
          mintedAt: new Date().toISOString(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          availability: "Active",
          sections: buildOptimisticQuestionnaireSections(input.sections),
          marks: { section: 0, item: 0, option: 0, score: 0 },
          scores: buildOptimisticQuestionnaireScores(input.scores ?? [], input.totalBands),
        }),
      );
    },
    persist: async (input, options) => {
      await getTRPCClient().catalog[
        performance ? "performanceMeasureDefinition" : "questionnaireDefinition"
      ].define.mutate(input, options);
    },
  });

  const reviseQuestionnaireDefinition = defineWrite<ReviseQuestionnaireDefinitionInput>({
    optimistic: (params) => {
      const input = reviseQuestionnaireDefinitionInputSchema.parse(params);
      const currentRow = currentResource(input.definitionUrl);
      if (currentRow.catalogVersion !== input.expectedVersion) {
        throw new Error(`Questionnaire definition '${input.definitionUrl}' changed before this revision was saved`);
      }
      const current = project(currentRow);
      const totalBands = input.totalBands ?? current.scores.find((score) => score.id === TOTAL_SCORE_ID)?.bands;
      collection().insert(
        optimisticRow({
          ...current,
          version: current.version + 1,
          mintedAt: new Date().toISOString(),
          sections: buildOptimisticQuestionnaireSections(input.sections),
          scores: input.scores
            ? buildOptimisticQuestionnaireScores(input.scores, totalBands)
            : [
                ...buildOptimisticQuestionnaireScores([], totalBands),
                ...current.scores.filter((score) => score.id !== TOTAL_SCORE_ID),
              ],
        }),
      );
    },
    persist: async (input, options) => {
      await getTRPCClient().catalog[
        performance ? "performanceMeasureDefinition" : "questionnaireDefinition"
      ].revise.mutate(input, options);
    },
  });

  const correctQuestionnaireDefinition = defineWrite<CorrectQuestionnaireDefinitionInput>({
    optimistic: (params) => {
      const input = correctQuestionnaireDefinitionInputSchema.parse(params);
      const resource = currentResource(input.definitionUrl);
      collection().update(syncKey(resource), (draft) => {
        if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
        if (input.description === null) delete draft.description;
        else if (input.description !== undefined) draft.description = input.description;
      });
    },
    persist: async (input, options) => {
      await getTRPCClient().catalog[
        performance ? "performanceMeasureDefinition" : "questionnaireDefinition"
      ].correct.mutate(input, options);
    },
  });

  const activateQuestionnaireDefinition = availabilityAction("activate");
  const deactivateQuestionnaireDefinition = availabilityAction("deactivate");

  function availabilityAction(intent: "activate" | "deactivate") {
    return defineWrite<ChangeQuestionnaireDefinitionAvailabilityInput>({
      optimistic: (params) => {
        const input = changeQuestionnaireDefinitionAvailabilityInputSchema.parse(params);
        const resource = currentResource(input.definitionUrl);
        collection().update(syncKey(resource), (draft) => {
          draft.status = intent === "activate" ? "active" : "retired";
        });
      },
      persist: async (input, options) => {
        await getTRPCClient().catalog[performance ? "performanceMeasureDefinition" : "questionnaireDefinition"][
          intent
        ].mutate(input, options);
      },
    });
  }

  function currentResource(url: string): QuestionnaireRow {
    let current: QuestionnaireRow | undefined;
    for (const candidate of collection().state.values()) {
      if (!matches(candidate) || candidate.url !== url) continue;
      if (!current || candidate.catalogVersion > current.catalogVersion) current = candidate;
    }
    if (!current?.id) throw new Error(`Questionnaire definition '${url}' is not available in the data store`);
    return current;
  }

  return {
    projectQuestionnaireDefinition,
    useQuestionnaireDefinitions,
    useQuestionnaireDefinition,
    defineQuestionnaireDefinition,
    reviseQuestionnaireDefinition,
    correctQuestionnaireDefinition,
    activateQuestionnaireDefinition,
    deactivateQuestionnaireDefinition,
  };
}
